import React, { useState, useRef } from "react";
import {
  Upload,
  FileSpreadsheet,
  Trash2,
  Send,
  CheckCircle2,
} from "lucide-react";
import * as XLSX from "xlsx";

const StockUpload = () => {
  const [data, setData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const COMPANYCODE = localStorage.getItem("companyCode") || "-";
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const requiredHeaders = [
    "Code",
    "description",
    "opnQty",
    "opCost",
    "Price1",
    "Price2",
    "Price3",
    "Oem",
    "Group",
    "SubGroup",
    "Category",
    "SubCategory",
    "Unit",
    "Modelno",
    "Origin",
    "Specification",
    "FixedCost",
  ];

  // Function to handle file parsing
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];

      // Convert to JSON
      const json = XLSX.utils.sheet_to_json(ws);
      setData(json);
      console.log("Parsed Data:", json);
    };

    reader.readAsBinaryString(file);
  };
  const numberFields = [
    "opnQty",
    "opCost",
    "Price1",
    "Price2",
    "Price3",
    "FixedCost",
  ];

  const handleSendToBackend = async () => {
    setLoading(true);
    try {
      const dataWithCompanyCode = data.map((item) => {
        const normalizedItem = {
          cmpcode: COMPANYCODE,
        };

        requiredHeaders.forEach((key) => {
          const value = item[key];

          if (numberFields.includes(key)) {
            normalizedItem[key] =
              value === undefined || value === null || value === ""
                ? 0
                : Number(value);
          } else {
            normalizedItem[key] =
              value === undefined || value === null ? "" : String(value).trim();
          }
        });

        return normalizedItem;
      });

      // ✅ Send array directly
      console.log(
        "Sending JSON to backend:",
        JSON.stringify(dataWithCompanyCode),
      );

      const response = await fetch(`${BASE_URL}/api/CRMProductUpload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataWithCompanyCode),
      });

      console.log("Response from backend:", response);

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      console.log("Result from backend:", result);
      alert("Stock data synced successfully!");
      clearFile();
    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => {
    setData([]);
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className=" bg-gray-50 px-6 py-3">
      <div className=" mx-auto">
        <header className="mb-8 flex justify-between items-end">
          <div>
            <h3 className="text-3xl font-bold text-gray-900">
              Inventory Master Upload
            </h3>
            <p className="text-gray-500">
              Preview and verify your stock data before syncing.
            </p>
          </div>
          {data.length > 0 && (
            <div className="flex gap-3">
              <button
                onClick={clearFile}
                className="flex items-center gap-2 px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
              >
                <Trash2 size={18} /> Clear
              </button>
              <button
                onClick={handleSendToBackend}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300"
              >
                {loading ? (
                  "Processing..."
                ) : (
                  <>
                    <Send size={18} /> Sync to Database
                  </>
                )}
              </button>
            </div>
          )}
        </header>

        {/* Upload Zone */}
        {data.length === 0 ? (
          <div
            onClick={() => fileInputRef.current.click()}
            className="border-2 border-dashed border-gray-300 bg-white rounded-2xl p-20 text-center hover:border-blue-400 transition-all cursor-pointer group"
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".xlsx, .xls"
              className="hidden"
            />
            <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
              <Upload size={40} />
            </div>
            <h4 className="text-xl font-semibold text-gray-800">
              Click to upload Inventory Excel
            </h4>
            <p className="text-gray-500 mt-2 max-w-xl mx-auto">
              Ensure your file contains the 17 required headers:
              <span className="font-mono text-xs block mt-2 text-gray-400 ">
                {requiredHeaders.join(", ")}
              </span>
            </p>
          </div>
        ) : (
          /* Data Preview Table */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FileSpreadsheet size={16} className="text-green-600" />{" "}
                {fileName} ({data.length} rows)
              </span>
              <span className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-full font-bold uppercase tracking-widest">
                Parsed Successfully
              </span>
            </div>
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 bg-white shadow-sm">
                  <tr>
                    {requiredHeaders.map((header) => (
                      <th
                        key={header}
                        className="p-3 text-[11px] font-bold text-gray-400 uppercase border-b border-gray-100"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 text-sm">
                  {data.slice(0, 50).map((row, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      {requiredHeaders.map((header) => (
                        <td
                          key={header}
                          title={row[header]}
                          className="p-3 text-gray-600 truncate max-w-[150px]"
                        >
                          {row[header] || (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.length > 50 && (
                <div className="p-4 text-center text-gray-400 text-xs italic border-t border-gray-50">
                  Showing first 50 rows only...
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockUpload;
